// FR-X4 in-meeting panel (DS artboard 3b, extension side panel). Shared slice with
// the Chrome Meet overlay (`clients/extension/src/content/meet.ts` + `src/live/mount.ts`).
// The overlay stays vanilla in a shadow root; this React panel is `/o/:slug/live-meeting`.

import { useEffect, useRef } from "react";
import { Badge, Button, Callout, Card, IconButton, RecPill, Utterance } from "@grackle/ui";
import type { LiveBookmark, LiveSessionStatus, TranscriptLine } from "./types";
import { formatElapsed, formatLineClock, speakerSlot } from "./types";
import "./live.css";

export interface MeetingCapturePanelProps {
  elapsedMs: number;
  status: LiveSessionStatus | string;
  lines: TranscriptLine[];
  bookmarks: LiveBookmark[];
  message?: string | null;
  speakerCount?: number;
  phaseHint?: string;
  onMark?: () => void;
  onStop?: () => void;
  onCollapse?: () => void;
  markDisabled?: boolean;
  stopLabel?: string;
}

// Utterance shows a speaker's first word only; a non-breaking space keeps "Speaker 3" whole so
// anonymous speakers stay distinguishable.
const speakerName = (label: string) => label.replace(/^(Speaker) (\d+)$/, "$1\u00a0$2");

export default function MeetingCapturePanel({
  elapsedMs,
  status,
  lines,
  bookmarks,
  message,
  speakerCount,
  phaseHint,
  onMark,
  onStop,
  onCollapse,
  markDisabled,
  stopLabel = "Stop",
}: MeetingCapturePanelProps) {
  const scroller = useRef<HTMLDivElement>(null);
  const uniqueSpeakers = new Set(lines.map((l) => l.speaker_label));
  const count = speakerCount ?? uniqueSpeakers.size;
  const last = lines[lines.length - 1];

  useEffect(() => {
    const el = scroller.current;
    if (el) el.scrollTop = el.scrollHeight;
  }, [lines.length, last?.text, last?.seq]);

  const hint =
    phaseHint ??
    (status === "paused"
      ? message ?? "Live transcript paused so the meeting stays smooth."
      : status === "unavailable"
        ? message ?? "Live transcription is unavailable. Recording continues."
        : status === "ready"
          ? "Transcript is attached to this grackle."
          : "Notes are ready a minute or two after the meeting ends.");

  // Coral is for recording only: a paused or unavailable session shows a plain badge instead.
  const time = formatElapsed(elapsedMs);
  const state =
    status === "paused" ? (
      <Badge tone="now" dot>
        Paused {time}
      </Badge>
    ) : status === "unavailable" || status === "failed" ? (
      <Badge dot>Off {time}</Badge>
    ) : (
      <RecPill time={time} />
    );

  return (
    <Card size="flush" className="live-panel" role="dialog" aria-label="Grackle recording">
      <div className="live-panel__head">
        {state}
        <Button className="live-panel__end" variant="now" size="sm" icon="flag" onClick={onMark} disabled={markDisabled || !onMark}>
          Mark moment
        </Button>
        {onCollapse ? <IconButton icon="x" label="Hide panel" size="sm" variant="muted" onClick={onCollapse} /> : null}
      </div>

      <div className="live-panel__section">
        <span className="gk-overline">Live transcript</span>
        {count > 0 ? <Badge size="sm">{count} speakers</Badge> : null}
      </div>

      {message && status !== "live" ? <Callout tone="now">{message}</Callout> : null}
      {bookmarks.length > 0 ? (
        <Callout tone="info">
          {bookmarks.length} marked moment{bookmarks.length === 1 ? "" : "s"}
        </Callout>
      ) : null}

      <div className="live-panel__lines" ref={scroller}>
        {lines.length === 0 ? (
          <p className="gk-sm">
            {status === "unavailable"
              ? "No live lines — transcription is off for this org, or no provider is connected."
              : "Listening… lines appear here as people talk."}
          </p>
        ) : (
          lines.map((line) => (
            <Utterance
              key={`${line.seq}-${line.is_final ? "f" : "p"}`}
              speaker={{ name: speakerName(line.speaker_label), speaker: speakerSlot(line.speaker_label) }}
              time={formatLineClock(line.t_start_ms)}
              text={line.text}
              live={!line.is_final}
            />
          ))
        )}
      </div>

      <div className="live-panel__foot">
        <span className="gk-caption">{hint}</span>
        {onStop ? (
          <Button variant="secondary" size="sm" onClick={onStop}>
            {stopLabel}
          </Button>
        ) : null}
      </div>
    </Card>
  );
}
